import React, {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    Button,
    CardBody,
    Checkbox,
    Menu,
    MenuHandler,
    MenuItem,
    MenuList,
    Tab,
    TabPanel,
    Tabs,
    TabsBody,
    TabsHeader,
    Typography
} from "@material-tailwind/react";
import DataTable, {HeaderInterface} from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {useUserContext} from "../../../types/contexts/UserContext";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {useToastContext} from "../../../types/contexts/ToastContext";
import CountryAvatar from "../../../components/CountryAvatar";
import {
    WithdrawTransactionsResponse,
    TransactionColumn,
    TransactionColumnResponse, WithdrawTransaction, WithdrawTransactionShort
} from "../../../types/Responses/TransactionsResponse";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import DropdownInput from "../../../components/DropdownInput";
import TableSkeleton from "../../../components/Skeletons/TableSkeleton";
import {UserShortInfo} from "../../../types/Responses/PayoutLimitsResponse";
import BrokerAvatar from "../../../components/BrokerAvatar";
import ProviderAvatar from "../../../components/ProviderAvatar";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import TransactionStatus from "../../../components/TransactionStatus";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import DatePickerMenu from "../../../components/DatePickerMenu";
import moment from "moment/moment";
import {ChartPeriod} from "../../../types/ChartPeriod";
import {Link, useNavigate, useParams} from "react-router-dom";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import TransactionDetail from "../../../components/TransactionDetail";
import OutlineInput from "../../../components/OutlineInput";
import Loader from "../../../assets/icons/Loader";
import {validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useDispatch} from "react-redux";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {Tooltip} from "../../../components/Tooltip";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {setSidebarTitle} from "../../../reducers/sidebar";
import FilterChips from "../../../components/FilterChips";
import {setFiltersOpen} from "../../../reducers/filters";
import {resolveTransactionTypeId} from "../../../utils/transactionTypes";
import {formatTransactionLogPayload, getTransactionLogTypeLabel} from "../../../utils/transactionLogs";
import ReportingSubscriptionModal from "../../../components/reporting/ReportingSubscriptionModal";
import {ReportingFilterLookups} from "../../../utils/reportingFilters";

type FilterFieldKey = 'status' | 'broker' | 'currency' | 'country' | 'provider' | 'user';

interface FilterFieldConfig {
    value: number[];
    func: React.Dispatch<React.SetStateAction<number[]>>;
}

interface FiltersState {
    broker: number[];
    user: number[];
    status: number[];
    currency: number[];
    country: number[];
    provider: number[];
    date_from: string;
    date_to: string;
}

type FiltersPayload = {
    broker: string;
    user: string;
    status: string;
    currency: string;
    country: string;
    provider: string;
    date_from: string;
    date_to: string;
};

type FilterChipType = FilterFieldKey | 'date';

interface FilterChipItem {
    key: string;
    label: string;
    type: FilterChipType;
    fieldKey?: FilterFieldKey;
    value?: number;
}

type WithdrawsRequestPayload = DataTableRequest & Partial<FiltersPayload>;
const Withdraws = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const user = useUserContext();
    const system = useSystemContext();
    const withdrawTypeId = useMemo(() => resolveTransactionTypeId(system?.system?.transactionTypes, 'Withdraw', 2), [system?.system?.transactionTypes]);
    const withdrawReportTypeName = 'Withdraw report';
    const handleError = useErrorHandler();
    const toast = useToastContext();
    const navigate = useNavigate();
    const dateFormat = 'YYYY-MM-DD';
    const getDefaultDateTo = useCallback(() => moment().endOf('week').format(dateFormat), [dateFormat]);
    const defaultDateFromValue = '1970-01-01';

    const [transactions, setTransactions] = useState<WithdrawTransactionsResponse>();
    const [brokers, setBrokers] = useState<UserShortInfo[]>()
    const [users, setUsers] = useState<UserShortInfo[]>()
    const [countries, setCountries] = useState<CountriesShortResponse>()
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [order, setOrder] = useState<TransactionColumnResponse>();

    const [withdrawModal, setWithdrawModal] = useState<WithdrawTransaction>();
    const [tab, setTab] = useState<"transaction" | "client" | "logs" | "user">("transaction");
    const [logsOpenAccordion, setLogsOpenAccordion] = useState<number>();

    const [inProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("")

    const [isForbidden, setIsForbidden] = useState<boolean>(false);

    const [defaultDateToValue, setDefaultDateToValue] = useState<string>(getDefaultDateTo());
    const [filterDateFrom, setFilterDateFrom] = useState<string>(defaultDateFromValue);
    const [filterDateTo, setFilterDateTo] = useState<string>(defaultDateToValue);
    const [filterStatus, setFilterStatus] = useState<number[]>([]);
    const [filterBroker, setFilterBroker] = useState<number[]>(id ? [parseInt(id)] : []);
    const [filterUser, setFilterUser] = useState<number[]>([]);
    const [filterCurrency, setFilterCurrency] = useState<number[]>([]);
    const [filterProvider, setFilterProvider] = useState<number[]>([]);
    const [filterCountry, setFilterCountry] = useState<number[]>([]);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const defaultDataTableRequest = useMemo<DataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 1
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<DataTableRequest>(defaultDataTableRequest);

    const modals = {
        details: useComponentVisible(false),
        share: useComponentVisible(false)
    }
    const subscriptionModal = useComponentVisible(false);
    const [subscriptionDefaults, setSubscriptionDefaults] = useState<{
        filters: FiltersPayload;
        typeName: string
    } | null>(null);

    const dispatch = useDispatch();

    const fields = useMemo<Record<FilterFieldKey, FilterFieldConfig>>(() => ({
        status: {
            value: filterStatus,
            func: setFilterStatus
        },
        broker: {
            value: filterBroker,
            func: setFilterBroker,
        },
        user: {
            value: filterUser,
            func: setFilterUser,
        },
        currency: {
            value: filterCurrency,
            func: setFilterCurrency
        },
        country: {
            value: filterCountry,
            func: setFilterCountry
        },
        provider: {
            value: filterProvider,
            func: setFilterProvider
        }
    }), [filterStatus, filterBroker, filterCurrency, filterCountry, filterProvider, filterUser]);

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('broker') && typeof request['broker'] !== "undefined" && request['broker'].length === 0)
            delete request['broker'];
        if (keys.includes('user') && typeof request['user'] !== "undefined" && request['user'].length === 0)
            delete request['user'];
        if (keys.includes('currency') && typeof request['currency'] !== "undefined" && request['currency'].length === 0)
            delete request['currency'];
        if (keys.includes('status') && typeof request['status'] !== "undefined" && request['status'].length === 0)
            delete request['status'];
        if (keys.includes('country') && typeof request['country'] !== "undefined" && request['country'].length === 0)
            delete request['country'];
        if (keys.includes('provider') && typeof request['provider'] !== "undefined" && request['provider'].length === 0)
            delete request['provider'];
        if (keys.includes('date_from') && typeof request['date_from'] !== "undefined" && request['date_from'].length === 0)
            delete request['date_from'];
        if (keys.includes('date_to') && typeof request['date_to'] !== "undefined" && request['date_to'].length === 0)
            delete request['date_to'];
        return request;
    }

    const getFiltersPayload = useCallback((overrideFilters?: Partial<FiltersState>): FiltersPayload => {
        const filtersState: FiltersState = {
            broker: overrideFilters?.broker ?? filterBroker,
            status: overrideFilters?.status ?? filterStatus,
            currency: overrideFilters?.currency ?? filterCurrency,
            country: overrideFilters?.country ?? filterCountry,
            provider: overrideFilters?.provider ?? filterProvider,
            user: overrideFilters?.user ?? filterUser,
            date_from: overrideFilters?.date_from ?? filterDateFrom,
            date_to: overrideFilters?.date_to ?? filterDateTo,
        };

        return {
            broker: filtersState.broker.join(','),
            status: filtersState.status.join(','),
            currency: filtersState.currency.join(','),
            country: filtersState.country.join(','),
            provider: filtersState.provider.join(','),
            user: filtersState.user.join(','),
            date_from: filtersState.date_from,
            date_to: filtersState.date_to,
        };
    }, [filterBroker, filterStatus, filterCurrency, filterUser, filterCountry, filterProvider, filterDateFrom, filterDateTo]);

    const sendRequest = useCallback((request: object) => {
        setIsDataTableLoading(true);
        persistDataTableRequest(request as DataTableRequest);
        API.getWithdrawTransactions(clearRequest(request)).then((response: AxiosResponse) => {
            setTransactions(response.data as WithdrawTransactionsResponse)
            setIsDataTableLoading(false);
        }).catch((e) => {
            setIsDataTableLoading(false);
            ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTIONS}`, toast, handleError, setIsForbidden)
        });
    }, [toast, handleError]);

    const applyFilters = useCallback((overrideFilters?: Partial<FiltersState>) => {
        sendRequest({
            ...dataTableRequest,
            ...getFiltersPayload(overrideFilters),
            page: 1
        });
    }, [dataTableRequest, getFiltersPayload, sendRequest]);

    const getBrokers = useCallback(() => {
        API.getBrokersList().then((response: AxiosResponse) => {
            setBrokers(response.data.data as UserShortInfo[]);
            // if (response.data.data.length === 1) {
            //     setFilterBroker([response.data.data[0].id]);
            // }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKERS_LIST}`, toast, handleError));
    }, [toast, handleError]);


    const getUsers = useCallback(() => {
        API.getUsersList(1).then((response: AxiosResponse) => {
            setUsers(response.data.data as UserShortInfo[]);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_USERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const getCountries = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            const temp = response.data as CountriesShortResponse;
            if (user && user.user && user.user.countries.length > 0) {
                setCountries({data: temp.data.filter(c => user.user.countries.includes(c.id))});
            } else setCountries(temp);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, user, handleError]);

    const getColumns = useCallback(() => {
        API.getTransactionColumns(withdrawTypeId).then((response: AxiosResponse) => {
            setOrder(response.data as TransactionColumnResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_TRANSACTION_COLUMNS}`, toast, handleError));
    }, [withdrawTypeId, toast, handleError]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        if (!request.search || request.search.length > 3) {
            const rq = {
                ...request,
                ...getFiltersPayload()
            };

            sendRequest(!rq.search ? {...rq, search: ''} : rq);
        } else {
            persistDataTableRequest(request as DataTableRequest);
        }
    }, [persistDataTableRequest, sendRequest, getFiltersPayload]);

    const onFilterChanged = useCallback((s: string | number, type: FilterFieldKey) => {
        const values = fields[type].value;
        fields[type].func([...values, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }))
    }, [fields]);

    const onFilterRemove = useCallback((index: number, type: FilterFieldKey) => {
        const field = fields[type];
        if (field.value) {
            const newValues = [...field.value];
            if (typeof newValues[index] !== "undefined") {
                newValues.splice(index, 1);
            }
            field.func(newValues);
        }
    }, [fields]);

    const onDateFromChanged = useCallback((v: string) => {
        setFilterDateFrom(v);
        if (moment(v).diff(moment(filterDateTo)) > 0) {
            setFilterDateTo(v);
        }
    }, [filterDateTo, setFilterDateFrom, setFilterDateTo]);

    const onSetupDates = useCallback((from?: string, to?: string) => {
        setFilterDateFrom(from ?? filterDateFrom);
        setFilterDateTo(to ?? filterDateTo);
    }, [filterDateFrom, filterDateTo]);

    const filter = useCallback(() => {
        dispatch(setFiltersOpen(false));
        setOpenFilters(false)
        applyFilters();
    }, [applyFilters, dispatch]);

    const reset = useCallback(() => {
        dispatch(setFiltersOpen(false));
        setOpenFilters(false);
        const nextDefaultDateTo = getDefaultDateTo();
        setFilterBroker([]);
        setFilterStatus([]);
        setFilterCurrency([]);
        setFilterUser([]);
        setFilterCountry([]);
        setFilterProvider([]);
        setFilterDateFrom(defaultDateFromValue);
        setFilterDateTo(nextDefaultDateTo);
        setDefaultDateToValue(nextDefaultDateTo);
        applyFilters({
            broker: [],
            status: [],
            currency: [],
            country: [],
            provider: [],
            user: [],
            date_from: defaultDateFromValue,
            date_to: nextDefaultDateTo,
        });
    }, [applyFilters, dispatch, defaultDateFromValue, getDefaultDateTo]);

    const removeFilterValue = useCallback((type: FilterFieldKey, value: number) => {
        const field = fields[type];
        const updatedValues = field.value.filter((v) => v !== value);
        field.func(updatedValues);
        return updatedValues;
    }, [fields]);

    const onColumnSelected = useCallback((e: ChangeEvent<HTMLInputElement> | undefined) => {
        if (e && e.target && order && order.data && system && system.system && system.system.columns) {
            setIsDataTableLoading(true);
            let values = [...order.data];
            if (order.data.map((c) => {
                return c.column
            }).includes(parseInt(e.target.value))) {
                values = values.filter(i => i.column !== parseInt(e.target.value));
                setOrder({data: values});
            } else {
                const column = system.system.columns.find(c => c.id === parseInt(e.target.value));
                if (column && column.is_withdraw)
                    values.push({column: column.id});
                const ord: TransactionColumn[] = [];
                const vl = values.map((v) => {
                    return v.column
                });
                for (let i = 0; i < system.system.columns.length; i++) {
                    // @ts-expect-error can be undefined
                    if (system.system.columns[i] && system.system.columns[i].is_withdraw && vl.includes(system.system.columns[i].id)) {
                        // @ts-expect-error can be undefined
                        ord.push({column: system.system.columns[i].id})
                    }
                }
                setOrder({data: ord});
            }
            if (values.length) {
                API.updateTransactionColumns(values, withdrawTypeId).then(() => setIsDataTableLoading(false)).catch((e) => {
                    setIsDataTableLoading(false);
                    ErrorHandler(e, `put${APILink.GET_TRANSACTION_COLUMNS}`, toast, handleError)
                });
            } else {
                setIsDataTableLoading(false);
            }
        }
    }, [order, system, withdrawTypeId, toast, isDataTableLoading, handleError]);

    const openModal = useCallback((withdraw: WithdrawTransactionShort) => {
        API.getWithdrawTransactionDetails(withdraw.id).then((response: AxiosResponse) => {
            setWithdrawModal(response.data as WithdrawTransaction);
            modals.details.setIsComponentVisible(true);
        }).catch((e) => {
            ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION_DETAILS.replace('$id', withdraw.id.toString())}`, toast, handleError)
        });
    }, [setWithdrawModal, modals.details.setIsComponentVisible]);

    const closeModal = useCallback(() => {
        modals.details.setIsComponentVisible(false);
        setWithdrawModal(undefined);
    }, []);

    const download = useCallback((format: 'csv' | 'xlsx') => {
        const request = {
            ...dataTableRequest,
            ...getFiltersPayload(),
            format,
        };
        setIsDataTableLoading(true);
        API.downloadWithdrawTransactions(clearRequest(request)).then((response: AxiosResponse) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', format === 'xlsx' ? "withdrawals.xlsx" : "withdrawals.csv");

            document.body.appendChild(link);
            link.click();

            // @ts-expect-error can be undefined
            link.parentNode.removeChild(link);
            setIsDataTableLoading(false);
        }).catch((e) => {
            setIsDataTableLoading(false);
            ErrorHandler(e, '', toast, handleError)
        });
    }, [dataTableRequest, getFiltersPayload, toast, handleError]);

    const onEmailChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const shareCallback = useCallback((b: boolean) => {
        if (b) {
            API.shareWithdrawReport(clearRequest({
                ...dataTableRequest,
                email,
                ...getFiltersPayload()
            })).then(() => {
                setInProgress(false);
                toast?.show(t('admin.reporting.deposits.sent', {email}), "bg-teal-500");
                modals.share.setIsComponentVisible(false)
            }).catch((e) => {
                setInProgress(false);
                ErrorHandler(e, `put${APILink.SHARE_WITHDRAW_REPORT}`, toast, handleError)
            });
        } else setInProgress(false);
    }, [modals.share, dataTableRequest, email, getFiltersPayload, toast, handleError, t]);

    const onShare = useCallback(() => {
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [
                    {
                        key: 'email',
                        label: t('admin.reporting.deposits.table.clients.email'),
                        type: InputType.email,
                        regex: '^.{3,}\\b$'
                    },
                ],
                introducedData: {email},
                callback: shareCallback
            }
        }));
    }, [email]);

    const setFiltersBasedOnRequest = useCallback((request: WithdrawsRequestPayload) => {
        if (typeof request['status'] !== 'undefined') {
            setFilterStatus(request['status'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['broker'] !== 'undefined') {
            setFilterBroker(request['broker'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['currency'] !== 'undefined') {
            setFilterCurrency(request['currency'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['country'] !== 'undefined') {
            setFilterCountry(request['country'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['user'] !== 'undefined') {
            setFilterUser(request['user'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['provider'] !== 'undefined') {
            setFilterProvider(request['provider'].split(',').map(v => parseInt(v)));
        }
        if (typeof request['date_from'] !== 'undefined') {
            setFilterDateFrom(request['date_from']);
        }
        if (typeof request['date_to'] !== 'undefined') {
            setFilterDateTo(request['date_to']);
        }
    }, []);

    useEffect(() => {
        setTitle(t('admin.reporting.withdraws.title'));
        dispatch(setSidebarTitle(t('admin.reporting.withdraws.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                getBrokers();
                getUsers();
                getCountries();
                getColumns();
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const newRequest = {
                            search: newID[1],
                            ...getFiltersPayload(),
                            ...dataTableRequest,
                        }
                        sendRequest(newRequest);
                        setFiltersBasedOnRequest(newRequest);
                    }
                } else {
                    const newRequest = {
                        ...getFiltersPayload(),
                        ...dataTableRequest,
                    };
                    sendRequest(newRequest);
                    setFiltersBasedOnRequest(newRequest);
                }
            }).catch();
        }
    }, [window.location.search, setFiltersBasedOnRequest, dispatch, getColumns]);

    const subscriptionMetadata = useMemo<ReportingFilterLookups>(() => ({
        brokers: (brokers ?? []).map((broker) => ({
            id: typeof broker.id === 'string' ? parseInt(broker.id, 10) : Number(broker.id),
            name: broker.name ?? broker.email ?? `#${broker.id}`
        })),
        users: (users ?? []).map((account) => ({
            id: typeof account.id === 'string' ? parseInt(account.id, 10) : Number(account.id),
            name: account.name ?? account.email ?? `#${account.id}`
        })),
        countries: countries ? countries.data.map((country) => ({
            id: country.id,
            name: country.name
        })) : undefined
    }), [brokers, users, countries]);

    const subscriptionModalUI = useMemo(() => {
        if (!subscriptionModal.isComponentVisible || !subscriptionDefaults) {
            return <></>;
        }
        return (
            <ReportingSubscriptionModal
                open={subscriptionModal.isComponentVisible}
                modalRef={subscriptionModal.ref}
                onClose={() => {
                    subscriptionModal.setIsComponentVisible(false);
                    setSubscriptionDefaults(null);
                }}
                defaultFilters={subscriptionDefaults.filters}
                defaultReportingTypeName={subscriptionDefaults.typeName}
                metadata={subscriptionMetadata}
            />
        );
    }, [subscriptionModal, subscriptionDefaults, subscriptionMetadata]);

    const shareModalUI = useMemo(() => {
        if (!modals.share.isComponentVisible)
            return <></>;

        return <Modal refer={modals.share.ref} showModal={modals.share.isComponentVisible}
                      title={t('admin.reporting.deposits.send_report')}>
            <div className="max-w-md w-full">
                <div className="text-center mb-4">
                    {t('admin.reporting.deposits.export')}
                </div>
                <OutlineInput name={t('admin.reporting.deposits.table.clients.email')} value={email} type="email"
                              fieldName="email" validated={validated}
                              onChange={onEmailChanged}/>
                <div className="text-center mt-8">
                    <Button onClick={() => onShare()} className="bg-teal-500" disabled={inProgress}>{inProgress ?
                        <Loader/> : t('admin.reporting.deposits.send')}</Button>
                </div>
            </div>
        </Modal>
    }, [modals.share, email, validated, onEmailChanged]);

    const toggleLogAccordion = useCallback((logId: number) => {
        setLogsOpenAccordion((current) => current === logId ? undefined : logId);
    }, []);

    const detailsModalUI = useMemo(() => {
        if (!withdrawModal || !modals.details.isComponentVisible || !brokers)
            return <></>;

        return <Modal refer={modals.details.ref} showModal={modals.details.isComponentVisible}
                      title={t('admin.reporting.deposits.modal.title', {id: withdrawModal.id})}>
            <div className="w-full">
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => closeModal()}/>

                <Tabs value={tab}>
                    <TabsHeader
                        className="rounded-none border-b border-blue-gray-50 bg-transparent p-0"
                        indicatorProps={{
                            className:
                                "bg-transparent border-b-2 border-gray-900 shadow-none rounded-none",
                        }}
                    >
                        <Tab key="transaction" value="transaction" onClick={() => setTab("transaction")}
                             className={tab === "transaction" ? "text-gray-900" : ""}>
                            {t('admin.reporting.deposits.transaction')}
                        </Tab>
                        <Tab key="client" value="client" onClick={() => setTab("client")}
                             className={tab === "client" ? "text-gray-900" : ""}>
                            {t('admin.reporting.deposits.client')}
                        </Tab>
                        {withdrawModal.user ? <Tab key="user" value="user" onClick={() => setTab("user")}
                                                   className={tab === "user" ? "text-gray-900" : ""}>
                            {t('admin.reporting.deposits.user')}
                        </Tab> : ''}
                        <Tab key="logs" value="logs" onClick={() => setTab("logs")}
                             className={tab === "logs" ? "text-gray-900" : ""}>
                            {t('admin.reporting.deposits.logs')}
                        </Tab>
                    </TabsHeader>
                    <TabsBody>
                        <TabPanel key="transaction" value="transaction">
                            <div className="w-full flex justify-between">
                                <div className="inline-block mr-2">
                                    <span className="w-fit flex items-center cursor-pointer hover:text-teal-500"
                                          onClick={() => user && user.user.permissions.includes('view users list') ? navigate(brokers.length > 1 ? `/app/admin/users/${withdrawModal.broker.id}/profile` : '/app/admin/profile') : ''}>
                                    <BrokerAvatar image={withdrawModal.broker.image} size="lg" className="h-12 w-12"
                                                  imageClasses="h-8 w-8"/> {withdrawModal.broker.name}
                                    </span>
                                </div>
                                <div className="inline-block">
                                    <span className="w-fit flex items-center">
                                    <ProviderAvatar imageSize="h-12 w-20"
                                                    image={`${window.location.origin}/images/psps/${withdrawModal.provider.image}`}/>
                                        {withdrawModal.provider.name}
                                    </span>
                                </div>
                            </div>
                            <h3 className="text-lg font-bold mt-12">{t('admin.reporting.deposits.details')}</h3>
                            <div className="text-sm font-light mt-6 grid grid-cols-1 lg:grid-cols-2 gap-2 items-center">
                                <TransactionDetail name={t('admin.reporting.deposits.table.status')}><TransactionStatus
                                    status={withdrawModal.status.name}/></TransactionDetail>
                                <TransactionDetail
                                    name={t('admin.reporting.deposits.table.date')}>{withdrawModal.datetime}</TransactionDetail>
                                <TransactionDetail
                                    name={t('admin.reporting.deposits.table.last_update')}>{withdrawModal.last_update}</TransactionDetail>
                                <TransactionDetail
                                    name={t('admin.reporting.deposits.table.amount')}>{withdrawModal.amount}</TransactionDetail>
                                <TransactionDetail
                                    name={t('admin.reporting.deposits.table.currency')}>{withdrawModal.currency.code}</TransactionDetail>
                                {withdrawModal.payment_transaction ? <TransactionDetail
                                    name={t('admin.reporting.deposits.table.payment_transaction')}>{withdrawModal.payment_transaction}</TransactionDetail> : ''}
                                {withdrawModal.old_cashier_id ? <TransactionDetail
                                    name={t('admin.reporting.deposits.table.old_cashier_id')}>{withdrawModal.old_cashier_id}</TransactionDetail> : ''}

                            </div>

                        </TabPanel>
                        <TabPanel key="client" value="client">
                            {withdrawModal.client.crm_client_id ?
                                <div>
                                    <Typography className="text-sm font-light mt-6 text-gray-400 text-right capitalize">CRM
                                        client id: <Link
                                            to={`/clients/${withdrawModal.client.crm_client_id}`}>{withdrawModal.client.crm_client_id ?? ''}</Link></Typography>
                                </div> : <></>
                            }
                            <div className="text-sm font-light mt-6 grid grid-cols-1 lg:grid-cols-2 gap-2 items-center">
                                <TransactionDetail
                                    name={t('admin.reporting.deposits.table.name')}>{withdrawModal.client.name ?? ''}</TransactionDetail>
                                <TransactionDetail name={t('admin.reporting.deposits.table.clients.email')}><a
                                    href={`mailto:${withdrawModal.client.email}`}>{withdrawModal.client.email}</a></TransactionDetail>
                                <TransactionDetail name={t('admin.reporting.deposits.table.country')}><CountryAvatar
                                    country={withdrawModal.client.country ?? ''}/></TransactionDetail>
                            </div>
                        </TabPanel>
                        {withdrawModal.user ? <TabPanel key="user" value="user">
                            <div className="text-sm font-light mt-6">
                                <div className="w-full flex justify-between items-center">
                                    <div className="inline-block mr-2">
                                    <span className="w-fit flex items-center cursor-pointer hover:text-teal-500"
                                          onClick={() => navigate(`/app/admin/users/${withdrawModal.user.id}/profile`)}>
                                    <BrokerAvatar image={withdrawModal.user.image} size="lg" className="h-12 w-12"
                                                  imageClasses="h-8 w-8"/> {withdrawModal.user.name}
                                    </span>
                                    </div>
                                    <div className="inline-block">
                                    <span className="w-fit flex items-center">
                                    <span className="text-teal-500 cursor-pointer" onClick={() => {
                                        setOpenFilters(false);
                                        modals.details.setIsComponentVisible(false);
                                        setFilterUser([parseInt(withdrawModal?.user.id) ?? 0]);
                                        sendRequest({
                                            ...dataTableRequest,
                                            user: parseInt(withdrawModal?.user.id) ?? 0
                                        })
                                    }}>View all withdraws made by {withdrawModal.user.name}</span>
                                    </span>
                                    </div>
                                </div>
                            </div>
                        </TabPanel> : ''}
                        <TabPanel key="logs" value="logs">
                            <div className="space-y-4 text-sm">
                                {withdrawModal.logs.map((log) => {
                                    const requestPayload = formatTransactionLogPayload(log.request);
                                    const responsePayload = formatTransactionLogPayload(log.response);
                                    const hasDetails = Boolean(log.details);
                                    const logTypeLabel = getTransactionLogTypeLabel(t, log.transaction_log_type?.name);

                                    return <Accordion className="w-full border border-blue-gray-50 dark:border-blue-gray-800 rounded-lg bg-gray-50 dark:bg-blue-gray-900/30"
                                                      key={log.id}
                                                      open={logsOpenAccordion === log.id}>
                                        <AccordionHeader
                                            className="w-full flex flex-col gap-1 text-left font-semibold text-gray-900 dark:text-gray-100"
                                            onClick={() => toggleLogAccordion(log.id)}>
                                            <span className="text-base">{logTypeLabel}</span>
                                            {hasDetails ?
                                                <span className="text-xs font-normal text-gray-600 dark:text-gray-400">{log.details}</span> : ''}
                                        </AccordionHeader>
                                        <AccordionBody>
                                            <div className="space-y-4 text-gray-700 dark:text-gray-200">
                                                {requestPayload ?
                                                    <div>
                                                        <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">{t('admin.reporting.deposits.request')}</span>
                                                        <pre className="mt-2 rounded-md bg-white dark:bg-blue-gray-900/60 p-3 font-mono text-xs whitespace-pre-wrap break-words max-h-80 overflow-auto">{requestPayload}</pre>
                                                    </div> : ''}
                                                {responsePayload ?
                                                    <div>
                                                        <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">{t('admin.reporting.deposits.response')}</span>
                                                        <pre className="mt-2 rounded-md bg-white dark:bg-blue-gray-900/60 p-3 font-mono text-xs whitespace-pre-wrap break-words max-h-80 overflow-auto">{responsePayload}</pre>
                                                    </div> : ''}
                                                {!requestPayload && !responsePayload && hasDetails ?
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{log.details}</p> : ''}
                                            </div>
                                        </AccordionBody>
                                    </Accordion>
                                })}
                            </div>
                        </TabPanel>
                    </TabsBody>
                </Tabs>
            </div>
        </Modal>
    }, [withdrawModal, modals.details.isComponentVisible, modals.details.ref, brokers, t, tab, closeModal, navigate, logsOpenAccordion, toggleLogAccordion, sendRequest, dataTableRequest, setFilterUser, setOpenFilters, modals.details.setIsComponentVisible]);


    const filterChips = useMemo<FilterChipItem[]>(() => {
        const chips: FilterChipItem[] = [];
        if (typeof system === 'undefined' || !system || typeof system.system === 'undefined' || !system.system)
            return chips;
        if (brokers && filterBroker.length) {
            const label = t('admin.reporting.deposits.table.broker');
            filterBroker.forEach((value) => {
                const broker = brokers.find((b) => Number(b.id) === value);
                if (broker) {
                    chips.push({
                        key: `broker-${value}`,
                        label: `${label}: ${broker.name}`,
                        type: 'broker',
                        fieldKey: 'broker',
                        value,
                    });
                }
            });
        }

        if (users && filterUser.length) {
            const label = t('admin.reporting.deposits.table.user');
            filterUser.forEach((value) => {
                const u = users.find((m) => Number(m.id) === value);
                if (u) {
                    chips.push({
                        key: `user-${value}`,
                        label: `${label}: ${u.name}`,
                        type: 'user',
                        fieldKey: 'user',
                        value,
                    });
                }
            });
        }

        if (system.system.providers && filterProvider.length) {
            const label = t('admin.reporting.deposits.table.method');
            filterProvider.forEach((value) => {
                const method = system?.system?.providers.find((m) => Number(m.id) === value);
                if (method) {
                    chips.push({
                        key: `provider-${value}`,
                        label: `${label}: ${method.name}`,
                        type: 'provider',
                        fieldKey: 'provider',
                        value,
                    });
                }
            });
        }
        if (system.system.transactionStatuses && filterStatus.length) {
            const label = t('admin.reporting.deposits.table.status');
            filterStatus.forEach((value) => {
                const status = system?.system?.transactionStatuses.find((s) => s.id === value);
                if (status) {
                    chips.push({
                        key: `status-${value}`,
                        label: `${label}: ${status.name}`,
                        type: 'status',
                        fieldKey: 'status',
                        value,
                    });
                }
            });
        }
        if (system.system.currencies && filterCurrency.length) {
            const label = t('admin.reporting.deposits.table.currency');
            filterCurrency.forEach((value) => {
                const currency = system?.system?.currencies.find((c) => c.id === value);
                if (currency) {
                    chips.push({
                        key: `currency-${value}`,
                        label: `${label}: ${currency.code}`,
                        type: 'currency',
                        fieldKey: 'currency',
                        value,
                    });
                }
            });
        }
        if (countries?.data && filterCountry.length) {
            const label = t('admin.reporting.deposits.table.country');
            filterCountry.forEach((value) => {
                const country = countries.data.find((c) => c.id === value);
                if (country) {
                    chips.push({
                        key: `country-${value}`,
                        label: `${label}: ${country.name}`,
                        type: 'country',
                        fieldKey: 'country',
                        value,
                    });
                }
            });
        }
        const hasCustomDateRange = filterDateFrom !== defaultDateFromValue || filterDateTo !== defaultDateToValue;
        if (hasCustomDateRange) {
            chips.push({
                key: 'date-range',
                label: `${t('admin.reporting.deposits.table.date')}: ${filterDateFrom} - ${filterDateTo}`,
                type: 'date',
            });
        }
        return chips;
    }, [brokers, filterBroker, countries, filterCountry, defaultDateFromValue, defaultDateToValue, filterCurrency, filterDateFrom, filterDateTo, filterProvider, filterUser, filterStatus, users, system, t]);

    const onChipRemove = useCallback((chip: FilterChipItem) => {
        if (chip.fieldKey && typeof chip.value !== "undefined") {
            const newValues = removeFilterValue(chip.fieldKey, chip.value);
            const overrides = {[chip.fieldKey]: newValues} as Partial<FiltersState>;
            applyFilters(overrides);
            return;
        }
        if (chip.type === 'date') {
            const nextDefaultDateTo = getDefaultDateTo();
            setFilterDateFrom(defaultDateFromValue);
            setFilterDateTo(nextDefaultDateTo);
            setDefaultDateToValue(nextDefaultDateTo);
            applyFilters({
                date_from: defaultDateFromValue,
                date_to: nextDefaultDateTo,
            });
        }
    }, [applyFilters, defaultDateFromValue, getDefaultDateTo, removeFilterValue, setDefaultDateToValue, setFilterDateFrom, setFilterDateTo]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    const renderedFilterChips = useMemo(() => {
        return filterChips.map((chip) => ({
            key: chip.key,
            label: chip.label,
            onRemove: () => onChipRemove(chip)
        }));
    }, [filterChips, onChipRemove]);

    const filters = useMemo(() => {
        if (!system || !system.system || !system.system.currencies || !system.system.transactionStatuses || !brokers || !system.system.providers || !countries || !users)
            return <></>

        return <>
            <div className="grid grid-cols-1 gap-0">
                {brokers.length > 1 ? <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]" list={brokers.map((broker) => {
                        return {
                            id: parseInt(broker.id),
                            name: broker.name,
                            icon: <BrokerAvatar className="h-6 max-w-[8rem]" image={broker.image} size="xs"/>
                        }
                    })} multiple={true} searchable={true} withLabel={true}
                                   name={t('admin.reporting.deposits.table.broker')} selected={filterBroker}
                                   onRemove={(i) => onFilterRemove(i, 'broker')}
                                   onChange={(s) => onFilterChanged(s, 'broker')}/>
                </div> : ''}
                {users.length ? <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]" list={users.map((user) => {
                        return {
                            id: parseInt(user.id),
                            name: user.name,
                            icon: <BrokerAvatar className="h-6 max-w-[8rem]" image={user.image} size="xs"/>
                        }
                    })} multiple={true} searchable={true} withLabel={true}
                                   name={t('admin.reporting.deposits.table.user')} selected={filterUser}
                                   onRemove={(i) => onFilterRemove(i, 'user')}
                                   onChange={(s) => onFilterChanged(s, 'user')}/>
                </div> : ''}
                <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]"
                                   list={system.system.providers.filter(p => p.allow_withdraw)} multiple={true}
                                   searchable={true} withLabel={true}
                                   name={t('admin.reporting.deposits.table.provider')}
                                   selected={filterProvider}
                                   onRemove={(i) => onFilterRemove(i, 'provider')}
                                   onChange={(s) => onFilterChanged(s, 'provider')}/>
                </div>
                <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]"
                                   list={system.system.transactionStatuses.filter((s => s.is_deposit))}
                                   multiple={true} searchable={true}
                                   withLabel={true} name={t('admin.reporting.deposits.table.status')}
                                   selected={filterStatus}
                                   onRemove={(i) => onFilterRemove(i, 'status')}
                                   onChange={(s) => onFilterChanged(s, 'status')}/>
                </div>
                <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]" list={system.system.currencies.map((c) => {
                        return {id: c.id, name: c.code};
                    })} multiple={true} searchable={true} withLabel={true}
                                   key={`deposit_currency`}
                                   name={t('admin.reporting.deposits.table.currency')} selected={filterCurrency}
                                   onRemove={(i) => onFilterRemove(i, 'currency')}
                                   onChange={(s) => onFilterChanged(s, 'currency')}/>
                </div>
                <div>
                    <DropdownInput buttonClassName="min-h-[auto!important]" list={countries.data.map((item) => {
                        return {
                            id: item.id,
                            name: item.name,
                            icon: <CountryAvatar country={item} withName={false} className="h-3 max-w-[1rem]"/>
                        }
                    })} multiple={true} searchable={true} withLabel={true}
                                   name={t('admin.reporting.deposits.table.country')}
                                   selected={filterCountry}
                                   onRemove={(i) => onFilterRemove(i, 'country')}
                                   onChange={(s) => onFilterChanged(s, 'country')}/>
                </div>
                <div>
                    <DatePickerMenu dateFormat={dateFormat} startDate={filterDateFrom} endDate={filterDateTo}
                                    showCustomisationButton={false}
                                    setEnd={setFilterDateTo} setStart={onDateFromChanged}
                                    showFilterButton={false}
                                    onChanged={onSetupDates}
                                    className="w-full"
                                    defaultPeriod={ChartPeriod.CUSTOM}/>
                </div>
            </div>
            <div className="mt-8 text-center">
                <Button className="text-white bg-teal-500"
                        onClick={() => filter()}>{t('admin.reporting.deposits.apply')}</Button>
                <span className="text-teal-500 ml-8 uppercase cursor-pointer"
                      onClick={() => reset()}>{t('admin.reporting.deposits.reset')}</span>
            </div>
        </>
    }, [openFilters, renderedFilterChips, system, brokers, users, filterUser, countries, filterBroker, filterStatus, filterCountry, filterCurrency, filterProvider, onFilterRemove, onFilterChanged, onDateFromChanged, onSetupDates, filter, reset]);

    const filterButtons = useMemo(() => {
        return <>
            <Button onClick={() => setOpenFilters(!openFilters)} className="bg-black px-0.5"><FontAwesomeIcon
                icon={solid('filter')} className="w-12 h-3"/></Button>
        </>
    }, [setOpenFilters, openFilters]);

    const openSubscriptionModal = useCallback(() => {
        setSubscriptionDefaults({
            filters: getFiltersPayload(),
            typeName: withdrawReportTypeName
        });
        subscriptionModal.setIsComponentVisible(true);
    }, [getFiltersPayload, withdrawReportTypeName, subscriptionModal]);

    const downloadButtons = useMemo(() => {
        return <>
            <Menu>
                <MenuHandler>
                    <Button className="bg-black px-0.5"><FontAwesomeIcon icon={solid('download')}
                                                                              className="w-12 h-3"/></Button>
                </MenuHandler>
                <MenuList className="dark:bg-gray-500 dark:text-blue-gray-900 dark:border-blue-gray-900">
                    <MenuItem onClick={() => download('csv')}><FontAwesomeIcon icon={solid('file-csv')}
                                                                               className="w-5 mr-2"/> {t('admin.reporting.deposits.download.csv')}
                    </MenuItem>
                    <MenuItem onClick={() => download('xlsx')}><FontAwesomeIcon icon={solid('file-excel')}
                                                                                className="w-5 mr-2"/> {t('admin.reporting.deposits.download.xlsx')}
                    </MenuItem>
                    <MenuItem onClick={() => modals.share.setIsComponentVisible(true)}><FontAwesomeIcon
                        icon={solid('share')} className="w-5 mr-2"/> {t('admin.reporting.deposits.download.share')}
                    </MenuItem>
                </MenuList>
            </Menu>
        </>
    }, [download, modals.share]);

    const canCreateSubscription = Boolean(user?.user?.permissions.includes('create reporting subscription'));

    const createSubscriptionButton = useMemo(() => {
        if (!canCreateSubscription) {
            return null;
        }
        return (
            <Button className="bg-black text-white px-3 py-2"
                    onClick={openSubscriptionModal}>
                {t('admin.reporting.subscriptions.create')}
            </Button>
        );
    }, [canCreateSubscription, openSubscriptionModal, t]);

    const actionsPanel = useMemo(() => (
        <div className="flex flex-row gap-2 lg:justify-end">
            <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5" onClick={() => refresh()}>
                <FontAwesomeIcon
                    icon={solid('refresh')}
                    className="w-12 h-3"/>
            </Button>
            {user?.user.permissions.includes('download withdraw transactions') ? downloadButtons : null}
            {createSubscriptionButton}
            {user?.user.permissions.includes('process withdraw') ?
                <Button onClick={() => navigate('/app/admin/withdraws/create')}
                        className="bg-black text-white">{t('admin.reporting.withdraws.new')}</Button> : ''}
        </div>
    ), [createSubscriptionButton, downloadButtons, user?.user.permissions]);

    const columnsButtons = useMemo(() => {
        if (!system || !system.system || !system.system.columns || !order)
            return <></>
        return <>
            <Menu dismiss={{itemPress: false}}>
                <MenuHandler>
                    <Button className="bg-black mx-2 px-0.5"><FontAwesomeIcon icon={solid('table-cells-large')}
                                                                              className="w-12 h-3"/></Button>
                </MenuHandler>
                <MenuList className="dark:bg-gray-500 dark:text-blue-gray-900 dark:border-blue-gray-900">
                    {system.system.columns.filter(column => column.is_withdraw).map(column =>
                        <MenuItem className="py-0" key={column.name}>
                            <Checkbox checked={order.data.map((c) => {
                                return c.column;
                            }).includes(column.id)}
                                      key={`check-${column.name}`}
                                      onChange={onColumnSelected}
                                      disabled={isDataTableLoading}
                                      color={'teal'}
                                      crossOrigin={''}
                                      value={column.id}
                                      label={<span
                                          className="dark:text-blue-gray-900 py-0.5">{t('admin.reporting.deposits.table.' + column.sortBy)}</span>}/>
                        </MenuItem>)}
                </MenuList>
            </Menu>
        </>
    }, [system, order, isDataTableLoading, onColumnSelected, t]);

    const dataTable = useMemo(() => {
        if (isForbidden)
            return <></>
        if (!transactions || !system || !system.system || !system.system.columns || !order || !user || !user.user || !brokers)
            return <TableSkeleton/>;

        const orderedColumns: HeaderInterface[] = [];
        for (let i = 0; i < order.data.length; i++) {
            // @ts-expect-error can be undefined
            const column = system.system.columns.find(c => c.id === order.data[i].column);
            if (column && column.is_withdraw && !['clients.email', 'crm_transaction_id', 'psp_transaction_id', 'reason'].includes(column.sortBy)) {
                let cp: HeaderInterface = {...column, className: 'text-center', legalSortBy: column.sortBy};
                cp = {
                    ...cp,
                    name: t('admin.reporting.deposits.table.' + cp.sortBy),
                    sortBy: !cp.sortBy?.toString().includes('.') || (dataTableRequest.search && dataTableRequest.search.length > 0) ? cp.sortBy : undefined,
                }
                if (column.name !== "Broker" || brokers.length > 1) {
                    orderedColumns.push(cp);
                }
            }
        }

        return <DataTable {...dataTableRequest}
                          total_pages={transactions.pages.total}
                          count={transactions.count}
                          nodata={transactions.data.length === 0}
                          inProgress={isDataTableLoading}
                          filters={filters}
                          extraFilters={columnsButtons}
                          rightPanel={actionsPanel}
                          fullWidthFilters={<FilterChips className="-mt-6 mb-6" items={renderedFilterChips}/>}
                          onChange={onDataTableChanged} headers={orderedColumns}>
            <tbody>
            {transactions.data.map((transaction) => {
                    return (<tr key={transaction.id}>
                        {orderedColumns.map((column) => {
                            if (column.sortBy === 'id') return <TD className="text-center">
                                <div className={`text-center ${transaction.old_cashier_id ? 'flex' : ''}`}>
                                    {transaction.old_cashier_id ? <Tooltip/> : ''}
                                    <b onClick={() => openModal(transaction)}
                                       className="cursor-pointer hover:text-teal-500">{transaction.id}</b>
                                </div>
                            </TD>
                            if (column.sortBy === 'broker_id') return <TD className="text-center truncate">
                                <div className="cursor-pointer hover:text-teal-500 flex items-center justify-center"
                                     onClick={() => user && user.user.permissions.includes('view users list') ? navigate(brokers.length > 1 ? `/app/admin/users/${transaction.broker.id}/profile` : '/app/admin/profile') : ''}>
                                    <BrokerAvatar image={transaction.broker.image}/>
                                    <span>{transaction.broker.name} </span>
                                </div>
                            </TD>
                            if (column.sortBy === 'psp_transaction_id') return <TD
                                className="text-center truncate">{transaction.psp_transaction_id ?? ''}</TD>
                            if (column.sortBy === 'created_at') return <TD
                                className="text-center w-[10%] truncate">{transaction.datetime}</TD>
                            if (column.sortBy === 'payment_method_id') return <TD
                                className="text-center max-w-[280px]">
                                <div className="flex items-center justify-center w-full h-full">
                                    <ProviderAvatar
                                        imageSize="w-12 h-8"
                                        image={`${window.location.origin}/images/psps/${transaction.provider.image}`}/>
                                    <div className="truncate">
                                        {transaction.provider.name}
                                    </div>
                                </div>
                            </TD>
                            if (column.legalSortBy === 'clients.first_name') return <TD
                                className="text-center truncate">{transaction.name}</TD>
                            if (column.legalSortBy === 'clients.email') return <TD
                                className="text-center truncate max-w-[150px]">{transaction.email}</TD>
                            if (column.legalSortBy === 'clients.country_id') return <TD
                                className="text-center truncate max-w-[150px]">
                                <div className="flex items-center justify-center">
                                    <CountryAvatar
                                        country={transaction.country}/>
                                </div>
                            </TD>
                            if (column.sortBy === 'amount') return <TD>
                                <div className="text-center">
                                    {transaction.amount}
                                </div>
                            </TD>
                            if (column.sortBy === 'currency_id') return <TD>
                                <div className="text-center">
                                    {transaction.currency.code}
                                </div>
                            </TD>
                            if (column.sortBy === 'transaction_status_id') return <TD className="text-center truncate">
                                <TransactionStatus status={transaction.status.name}/>
                            </TD>

                            return <></>
                        })}
                    </tr>)
                }
            )}
            </tbody>
        </DataTable>
    }, [system, dataTableRequest, isDataTableLoading, transactions, downloadButtons, filterButtons, columnsButtons, filters, order, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="page-card-block mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {detailsModalUI}
                    {subscriptionModalUI}
                    {shareModalUI}
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Withdraws);
